import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { apiClient } from '@/lib/api/client'
import { fraudClient } from '@/lib/api/fraud-client'
import { FRAUD_PROFILES, type FraudProfile } from '@/lib/api/fraud-types'
import type { Event } from '@/lib/api/types'
import { useAuth } from '@/lib/auth'
import { DEFAULT_EVENT_TIMEZONE, eventLocalToInstant } from '@/lib/eventTime'

export const Route = createFileRoute('/_authenticated/ops/events/new')({
  // Superuser gating lives on the /ops layout route (ops.tsx).
  component: NewEventPage,
  validateSearch: (search: Record<string, unknown>): { from?: number } => {
    const from = Number(search.from)
    return Number.isFinite(from) && from > 0 ? { from } : {}
  },
})

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50)
}

const inputCls =
  'block w-full mt-1 px-3 py-2 text-sm border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100'
const labelCls = 'text-xs font-medium text-neutral-600 dark:text-neutral-400'

function NewEventPage() {
  const { token } = useAuth()
  const navigate = useNavigate()
  const { from } = Route.useSearch()
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string> | null>(null)

  const { data: organizations } = useQuery({
    queryKey: ['ops-organizations'],
    queryFn: () => apiClient.getOrganizationsList(token ?? ''),
    enabled: !!token,
  })

  const { data: pastEvents } = useQuery({
    queryKey: ['ops-events-brief'],
    queryFn: () => apiClient.getEventsBrief(token ?? ''),
    enabled: !!token,
  })

  // Duplicate-prefill: the source event's configuration seeds the form; the
  // user edits (name, dates) and a single POST creates the new event. Nothing
  // is staged server-side, so an abandoned form leaves no half-created rows.
  const { data: source, isFetching: loadingSource } = useQuery({
    queryKey: ['ops-event-source', from],
    queryFn: () => apiClient.getEventAny(token ?? '', from as number),
    enabled: !!token && !!from,
  })

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!token || submitting) return
    setErrors(null)

    const formData = new FormData(e.currentTarget)
    const get = (key: string) => ((formData.get(key) as string) ?? '').trim()

    const name = get('name')
    const timezone = get('timezone') || DEFAULT_EVENT_TIMEZONE
    const payload: Record<string, unknown> = {
      name,
      slug: slugify(name),
      email: get('email'),
      timezone,
      organization: Number(get('organization')) || null,
      event_type: get('event_type'),
      description: get('description') || null,
      currency: get('currency'),
      token_value: get('token_value'),
      token_name: get('token_name'),
      token_symbol: get('token_symbol'),
      topup_amounts: get('topup_amounts'),
      // Tax/additional are stored as FRACTIONS (0.15 = 15%) — FeeCalculator
      // multiplies the stored values directly; the form takes percents and
      // converts, mirroring the Settings page.
      tax_percentage: Number(get('tax_percentage') || 0) / 100,
      tax_paid_by: get('tax_paid_by'),
      additional_fee_percentage: Number(get('additional_fee_percentage') || 0) / 100,
      // Also a fraction for every event type: the token path (FeeCalculator)
      // and, since backend #654, the Direct Pay calculator both multiply the
      // stored value directly (0.0275 = 2.75%).
      platform_fee_percentage: Number(get('platform_fee_percentage') || 0) / 100,
      // Location: null rather than '' — these are null=True but not
      // blank=True server-side (matches the Settings page convention).
      venue_name: get('venue_name') || null,
      address: get('address') || null,
      city: get('city') || null,
      zip_code: get('zip_code') || null,
      country: get('country') || null,
    }

    // Naive datetime-local values are venue wall-clock in the timezone chosen
    // on THIS form — never the admin's browser timezone (see eventTime.ts).
    const dates: Array<[string, boolean]> = [
      ['start_date_time', true],
      ['end_date_time', true],
      ['publish_start_date_time', true],
      ['publish_end_date_time', false],
      ['end_token_sales_date_time', false],
      ['signup_end_date_time', false],
    ]
    for (const [field, required] of dates) {
      const raw = get(field)
      if (raw) payload[field] = eventLocalToInstant(raw, timezone)
      else if (required) {
        setErrors({ [field]: 'Required.' })
        return
      }
    }

    setSubmitting(true)
    try {
      const created = await apiClient.createEvent(token, payload)
      // The event got a fraud policy on the platform's default profile the
      // moment it was created; put the profile chosen here on top.
      try {
        await fraudClient.updatePolicy(token, created.id, { profile: get('fraud_profile') as FraudProfile })
      } catch {
        // The event exists. Land on its policy tab so the profile is set by hand.
        navigate({ to: '/events/$eventId/fraud', params: { eventId: String(created.id) }, search: { tab: 'policy' } })
        return
      }
      navigate({ to: '/events/$eventId/settings', params: { eventId: String(created.id) } })
    } catch (err) {
      // DRF returns {field: [messages]} — surface per-field where possible.
      const detail = err instanceof Error ? err.message : 'Create failed'
      setErrors({ _global: detail })
      setSubmitting(false)
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">New event</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
          Creates the event configuration; images, branding, map and vendors are set up afterwards
          on the event&apos;s Settings page.
        </p>
      </div>

      {/* Start from a past event */}
      <div className="mb-6 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-4">
        <EventPicker
          events={pastEvents}
          selected={source ?? null}
          loading={loadingSource}
          onSelect={(id) =>
            navigate({
              to: '/ops/events/new',
              search: id ? { from: id } : {},
              replace: true,
            })
          }
        />
        <p className="mt-2 text-xs text-neutral-400 dark:text-neutral-500">
          Copies the event configuration only — vendors, menus and pages are not duplicated. Dates
          are left blank for you to set.
        </p>
      </div>

      {/* key remounts the form when the source changes so defaultValues re-seed */}
      <form key={source?.id ?? 'blank'} onSubmit={handleSubmit} className="space-y-6">
        <Section title="Basics">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className={labelCls}>
              Event name *
              <input name="name" required maxLength={100} className={inputCls}
                defaultValue={source ? `${source.name} (copy)` : ''} />
            </label>
            <label className={labelCls}>
              Organization *
              <select name="organization" required className={inputCls}
                defaultValue={source?.organization
                  ? String(typeof source.organization === 'object' ? source.organization.id : source.organization)
                  : ''}>
                <option value="">Select…</option>
                {organizations?.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </label>
            <label className={labelCls}>
              Contact email *
              <input name="email" type="email" required className={inputCls}
                defaultValue={source?.email ?? ''} />
            </label>
            <label className={labelCls}>
              Event type
              <select name="event_type" className={inputCls} defaultValue={source?.event_type ?? 'token'}>
                <option value="token">Token Event</option>
                <option value="direct_pay">Direct Pay Event</option>
                <option value="sampling">Sampling Event</option>
              </select>
            </label>
            <label className={`${labelCls} sm:col-span-2`}>
              Description
              <textarea name="description" rows={2} className={inputCls}
                defaultValue={source?.description ?? ''} />
            </label>
          </div>
        </Section>

        <Section title="Schedule" subtitle="All times are the venue's local wall-clock in the selected timezone.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className={labelCls}>
              Timezone
              <select name="timezone" className={inputCls}
                defaultValue={source?.timezone ?? DEFAULT_EVENT_TIMEZONE}>
                <option value="America/Toronto">America/Toronto (Eastern)</option>
                <option value="America/Vancouver">America/Vancouver (Pacific)</option>
                <option value="America/New_York">America/New_York (Eastern)</option>
                <option value="America/Los_Angeles">America/Los_Angeles (Pacific)</option>
                <option value="America/Chicago">America/Chicago (Central)</option>
                <option value="America/Denver">America/Denver (Mountain)</option>
                <option value="America/Edmonton">America/Edmonton (Mountain)</option>
                <option value="America/Winnipeg">America/Winnipeg (Central)</option>
                <option value="America/Halifax">America/Halifax (Atlantic)</option>
              </select>
            </label>
            <div />
            <DateField name="start_date_time" label="Event start *" required error={errors?.start_date_time} />
            <DateField name="end_date_time" label="Event end *" required error={errors?.end_date_time} />
            <DateField name="publish_start_date_time" label="Publish start *" required error={errors?.publish_start_date_time} />
            <DateField name="publish_end_date_time" label="Publish end" />
            <DateField name="end_token_sales_date_time" label="Token sales end" />
            <DateField name="signup_end_date_time" label="Signups end" />
          </div>
        </Section>

        <Section title="Tokens &amp; Money">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <label className={labelCls}>
              Currency
              <select name="currency" className={inputCls} defaultValue={source?.currency ?? 'CAD'}>
                <option value="CAD">CAD</option>
                <option value="USD">USD</option>
              </select>
            </label>
            <label className={labelCls}>
              Token value *
              <input name="token_value" required type="number" step="0.01" min="0.01" className={inputCls}
                defaultValue={source?.token_value ?? ''} placeholder="2.50" />
            </label>
            <label className={labelCls}>
              Token name *
              <input name="token_name" required maxLength={50} className={inputCls}
                defaultValue={source?.token_name ?? ''} placeholder="Tokens" />
            </label>
            <label className={labelCls}>
              Token symbol
              <input name="token_symbol" maxLength={50} className={inputCls}
                defaultValue={source?.token_symbol ?? ''} />
            </label>
            <label className={`${labelCls} col-span-2`}>
              Top-up amounts (comma-separated tokens)
              <input name="topup_amounts" className={inputCls}
                defaultValue={source?.topup_amounts ?? '5,10,15'} />
            </label>
            <label className={labelCls}>
              Tax %
              <input name="tax_percentage" type="number" step="0.01" min="0" className={inputCls}
                defaultValue={source?.tax_percentage != null
                  ? (source.tax_percentage * 100).toFixed(2)
                  : 13} />
            </label>
            <label className={labelCls}>
              Tax paid by
              <select name="tax_paid_by" className={inputCls} defaultValue={source?.tax_paid_by ?? 'user'}>
                <option value="user">Customer</option>
                <option value="event">Organizer</option>
              </select>
            </label>
            <label className={labelCls}>
              Additional fee %
              <input name="additional_fee_percentage" type="number" step="0.01" min="0" className={inputCls}
                defaultValue={source?.additional_fee_percentage != null
                  ? (source.additional_fee_percentage * 100).toFixed(2)
                  : 0} />
            </label>
            <label className={labelCls}>
              EventPay fee %
              <input name="platform_fee_percentage" type="number" step="0.01" min="0" className={inputCls}
                defaultValue={source?.platform_fee_percentage != null
                  ? Math.round(source.platform_fee_percentage * 100 * 100) / 100
                  : 2.75} />
              <span className="block mt-1 text-[10px] text-neutral-400 dark:text-neutral-500">
                All fee/tax fields are entered as percents.
              </span>
            </label>
          </div>
        </Section>

        <Section title="Risk controls">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className={labelCls}>
              Risk profile
              <select name="fraud_profile" className={inputCls} defaultValue="standard">
                {FRAUD_PROFILES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              <span className="block mt-1 text-[10px] text-neutral-400 dark:text-neutral-500">
                Decides when a card reload needs 3-D Secure and when it is refused. Change it any time on the event's Risk → Policy tab,
                and trust known attendees or cards on its Trusted tab. Enforcement follows the platform setting unless the event overrides it on the Policy tab.
              </span>
            </label>
            <div className="text-xs text-neutral-500 dark:text-neutral-400 space-y-1 sm:pt-5">
              {FRAUD_PROFILES.map((p) => <p key={p.value}><span className="font-medium text-neutral-700 dark:text-neutral-300">{p.label.split(' (')[0]}:</span> {p.description}</p>)}
            </div>
          </div>
        </Section>

        <Section title="Location">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className={labelCls}>
              Venue name
              <input name="venue_name" className={inputCls} defaultValue={source?.venue_name ?? ''} />
            </label>
            <label className={labelCls}>
              Address
              <input name="address" className={inputCls} defaultValue={source?.address ?? ''} />
            </label>
            <label className={labelCls}>
              City
              <input name="city" className={inputCls} defaultValue={source?.city ?? ''} />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className={labelCls}>
                Postal code
                <input name="zip_code" className={inputCls} defaultValue={source?.zip_code ?? ''} />
              </label>
              <label className={labelCls}>
                Country
                <input name="country" className={inputCls} defaultValue={source?.country ?? 'Canada'} />
              </label>
            </div>
          </div>
        </Section>

        {errors?._global && (
          <p className="text-sm text-red-600 dark:text-red-400">{errors._global}</p>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Create event'}
          </button>
          <span className="text-xs text-neutral-400 dark:text-neutral-500">
            You&apos;ll land on the new event&apos;s Settings page to finish setup.
          </span>
        </div>
      </form>
    </div>
  )
}

function Section({ title, subtitle, children }: {
  title: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-5">
      <h2 className="text-sm font-semibold text-neutral-900 dark:text-white mb-1">{title}</h2>
      {subtitle && <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">{subtitle}</p>}
      {!subtitle && <div className="mb-3" />}
      {children}
    </div>
  )
}

function EventPicker({ events, selected, loading, onSelect }: {
  events: Event[] | undefined
  selected: Event | null
  loading: boolean
  onSelect: (id: number | null) => void
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return (events ?? []).slice(0, 8)
    return (events ?? []).filter((ev) => ev.name.toLowerCase().includes(q)).slice(0, 8)
  }, [events, query])

  if (selected) {
    return (
      <div>
        <p className={labelCls}>Starting from</p>
        <div className="mt-1 flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40">
          <span className="text-sm font-medium text-neutral-900 dark:text-white truncate">
            {selected.name}
            {selected.start_date_time && (
              <span className="ml-2 text-xs font-normal text-neutral-500 dark:text-neutral-400">
                {selected.start_date_time.slice(0, 10)}
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={() => { setQuery(''); onSelect(null) }}
            className="text-xs font-medium text-neutral-500 hover:text-red-600 dark:text-neutral-400 dark:hover:text-red-400 shrink-0"
          >
            Clear
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      <label className={labelCls}>
        Start from a past event (optional)
        <input
          className={inputCls}
          placeholder="Search events…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
      </label>
      {loading && (
        <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">Loading source event…</p>
      )}
      {open && matches.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-lg divide-y divide-neutral-100 dark:divide-neutral-800">
          {matches.map((ev) => (
            <li key={ev.id}>
              <button
                type="button"
                // mousedown fires before the input's blur closes the panel
                onMouseDown={(e) => { e.preventDefault(); onSelect(ev.id) }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800"
              >
                <span className="font-medium text-neutral-900 dark:text-white">{ev.name}</span>
                {ev.start_date_time && (
                  <span className="ml-2 text-xs text-neutral-500 dark:text-neutral-400">
                    {ev.start_date_time.slice(0, 10)}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function DateField({ name, label, required, error }: {
  name: string
  label: string
  required?: boolean
  error?: string
}) {
  return (
    <label className={labelCls}>
      {label}
      <input name={name} type="datetime-local" required={required} className={inputCls} />
      {error && <span className="block mt-1 text-xs text-red-600 dark:text-red-400">{error}</span>}
    </label>
  )
}
